import React, { useRef, useState } from "react";
import { Button, Upload, message, Modal } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import UploadCustomListItemPreviewTable from "./UploadCustomListItemPreviewTable";
import { CustomListItemService } from "../../../Services/CustomListItemService";
import { WebApiCustomListsDtoCustomListBulkItemDto, WebApiModelsCustomListField, WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto } from "../../../Repository/eventbookyapi";
import { UiControlType } from '../../../Types/UiControlType';
import { excelSerialToDate, isExcelSerialDate } from "../../../Helpers/DateHelpers";

interface UploadCustomListItemButtonProps {
    listId: number;
    members: WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto[]
    fieldSettings: WebApiModelsCustomListField[];
    onUploadSuccess?: () => void;
}

const UploadCustomListItemButton: React.FC<UploadCustomListItemButtonProps> = ({
    listId,
    members,
    fieldSettings,
    onUploadSuccess,
}) => {
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [modalVisible, setModalVisible] = useState(false);

    const handleFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

            setPreviewData(jsonData as any[]);
            setModalVisible(true);
        };
        reader.readAsArrayBuffer(file);
        return false; // Prevent upload
    };

    const handleUpload = async () => {
        try {
            // Map Excel columns (nameAdmin) to fieldId and build items
            const items: WebApiCustomListsDtoCustomListBulkItemDto[] = previewData.map((row) => {
                const fieldValues = fieldSettings
                    .filter(f => f.nameAdmin)
                    .map(f => {
                        let value = row[f.nameAdmin as string] ?? "";

                        // Handle Date fields: convert Excel serial numbers
                        if (f.uiControl === UiControlType.Date && value) {
                            if (isExcelSerialDate(value)) {
                                const date = excelSerialToDate(value);
                                value = date?.toISOString() || value;
                            }
                        }

                        // Handle Dropdown: allow both id and value
                        if (f.uiControl === UiControlType.Dropdown && f.alternatives) {
                            const alternatives = f.alternatives.split(';');
                            // If value is a string and not a number, try to find its index (id)
                            if (value && isNaN(Number(value))) {
                                const idx = alternatives.findIndex(opt => opt.trim() === value.trim());
                                if (idx !== -1) {
                                    value = alternatives[idx]; // keep as value
                                }
                            } else if (value && !isNaN(Number(value))) {
                                // If value is a number, treat as index (1-based or 0-based?)
                                const idx = Number(value);
                                if (alternatives[idx] !== undefined) {
                                    value = alternatives[idx];
                                }
                            }
                        }
                        return {
                            fieldId: f.id,
                            fieldValue: value,
                        };
                    });
                // Add memberId if present in row
                let memberUniqueId = row["memberId"] ?? row["medlemsid"];
                let memberId = members.find(m => String(m.uniqueId) === String(memberUniqueId))?.memberId;

                return memberId !== undefined
                    ? { listId, fieldValues, memberId }
                    : { listId, fieldValues };
            });

            await CustomListItemService.CreateBulkCustomItems({ listId, items });
            setModalVisible(false);
            setPreviewData([]);
            onUploadSuccess?.();
        } catch (e) {
            message.error("Import misslyckades!");
        }
    };

    return (
        <>
            <Upload
                accept=".xlsx,.xls"
                showUploadList={false}
                beforeUpload={handleFile}
            >
                <Button style={{ marginLeft: 8, marginBottom: 16 }}
                    icon={<UploadOutlined />}>Importera</Button>
            </Upload>
            <Modal
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={handleUpload}
                okText="Importera"
                cancelText="Avbryt"
                width={800}
            >
                <UploadCustomListItemPreviewTable
                    data={previewData}
                    fieldSettings={fieldSettings}
                    showMemberIdColumn={true}
                />
            </Modal>
        </>
    );
};

export default UploadCustomListItemButton;